import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const DIVIDEND_TAX_MODES = [
  { value: 'corporatif', label: 'Avec impôt sur dividendes' },
  { value: 'personnel', label: 'Sans impôt sur dividendes' },
];

function normalizeDestinations(destinations) {
  const safe = (destinations || [])
    .map(d => ({
      assetId: d?.assetId || '',
      percent: parseFloat(d?.percent) || 0,
    }))
    .filter(d => d.assetId);

  const sum = safe.reduce((s, d) => s + (d.percent || 0), 0);
  if (sum <= 0) return safe;
  const factor = 100 / sum;
  return safe.map(d => ({ ...d, percent: d.percent * factor }));
}

const CashflowDirectionInitial = ({ initialEvent, onSave, entities = [], assets = [] }) => {
  const initialDetails = initialEvent?.details || {};

  const entitiesById = useMemo(() => {
    const m = {};
    for (const e of entities) m[e.id] = e;
    return m;
  }, [entities]);

  const assetTypesByEntityId = useMemo(() => {
    const map = {};
    for (const a of assets) {
      if (!map[a.entityId]) map[a.entityId] = new Set();
      map[a.entityId].add(a.type);
    }
    return map;
  }, [assets]);

  const [routingBySource, setRoutingBySource] = useState(initialDetails.routingBySource || {});

  useEffect(() => {
    setRoutingBySource(initialDetails.routingBySource || {});
  }, [initialEvent?.id, initialDetails?.routingBySource]);

  // Ensure we have an entry for every current entity (so the UI doesn't “lose” rows)
  useEffect(() => {
    setRoutingBySource(prev => {
      const next = { ...(prev || {}) };
      for (const source of entities) {
        if (!next[source.id]) {
          next[source.id] = {
            dividendTaxMode: source.type === 'corporation' ? 'corporatif' : 'personnel',
            destinations: [],
          };
        }
      }
      return next;
    });
  }, [entities, assetTypesByEntityId]);

  const updateSourceRouting = (sourceId, patch) => {
    setRoutingBySource(prev => ({
      ...(prev || {}),
      [sourceId]: { ...(prev?.[sourceId] || {}), ...patch },
    }));
  };

  const updateDestination = (sourceId, idx, patch) => {
    const current = routingBySource?.[sourceId] || {};
    const destinations = [...(current.destinations || [])];
    destinations[idx] = { ...(destinations[idx] || {}), ...patch };
    updateSourceRouting(sourceId, { destinations });
  };

  const addDestination = (sourceId) => {
    const current = routingBySource?.[sourceId] || {};
    const destinations = [...(current.destinations || [])];
    destinations.push({ assetId: '', percent: destinations.length === 0 ? 100 : 0 });
    updateSourceRouting(sourceId, { destinations });
  };

  const removeDestination = (sourceId, idx) => {
    const current = routingBySource?.[sourceId] || {};
    const destinations = [...(current.destinations || [])];
    destinations.splice(idx, 1);
    updateSourceRouting(sourceId, { destinations });
  };

  const handleSubmit = () => {
    const safeRouting = {};
    for (const [sourceId, r] of Object.entries(routingBySource || {})) {
      safeRouting[sourceId] = {
        dividendTaxMode: r?.dividendTaxMode === 'personnel' ? 'personnel' : 'corporatif',
        destinations: normalizeDestinations(r?.destinations || []),
      };
    }

    onSave({ routingBySource: safeRouting });
  };

  const accountOptions = useMemo(() => {
    const eligible = (assets || []).filter(a => a.type !== 'immobilier');
    return eligible.map(a => {
      const ent = entitiesById[a.entityId];
      const entLabel = ent ? `${ent.name} (${ent.type === 'personnel' ? 'Personnel' : 'Corporation'})` : 'Sans détenteur';
      return {
        value: a.id,
        label: `${a.name} — ${entLabel}`,
      };
    });
  }, [assets, entitiesById]);

  return (
    <div data-testid="cashflow-direction-initial">

      {entities.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ajoutez au moins un détenteur pour configurer la direction du cashflow.</p>
      ) : (
        <div className="space-y-4">
          {entities.map(source => {
            const r = routingBySource?.[source.id] || {};
            const destinations = r?.destinations || [];
            const hasPersonalDestination = destinations.some(d => {
              const asset = assets.find(a => a.id === d.assetId);
              const ent = asset ? entitiesById[asset.entityId] : null;
              return source.type === 'corporation' && ent?.type === 'personnel';
            });

            return (
              <div key={source.id} className="border border-border rounded-lg p-4 bg-muted/20" data-testid={`cashflow-routing-${source.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Source: {source.name}
                      <span className="text-xs text-muted-foreground ml-2">
                        ({source.type === 'personnel' ? 'Personnel' : 'Corporation'})
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Sélectionnez les comptes (Actifs) où envoyer le cashflow.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Select
                    label="Dividendes si transfert corporatif → personnel"
                    value={r?.dividendTaxMode || 'corporatif'}
                    onChange={v => updateSourceRouting(source.id, { dividendTaxMode: v })}
                    options={DIVIDEND_TAX_MODES}
                    disabled={!hasPersonalDestination}
                    data-testid={`cashflow-dividend-mode-${source.id}`}
                  />
                </div>

                <div className="space-y-2 mt-3">
                  {destinations.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Aucun compte sélectionné.</p>
                  ) : (
                    destinations.map((d, idx) => (
                      <div key={`${source.id}-${idx}`} className="grid grid-cols-1 md:grid-cols-[1fr_140px_40px] gap-2 items-end">
                        <Select
                          label={idx === 0 ? 'Compte' : ''}
                          value={d.assetId || ''}
                          onChange={(v) => updateDestination(source.id, idx, { assetId: v })}
                          options={[{ value: '', label: 'Sélectionner...' }, ...accountOptions]}
                          data-testid={`cashflow-dest-account-${source.id}-${idx}`}
                        />
                        <Input
                          label={idx === 0 ? 'Répartition (%)' : ''}
                          type="number"
                          value={d.percent ?? 0}
                          onChange={(e) => updateDestination(source.id, idx, { percent: e.target.value })}
                          data-testid={`cashflow-dest-percent-${source.id}-${idx}`}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          iconName="Trash2"
                          onClick={() => removeDestination(source.id, idx)}
                          style={{ color: 'var(--color-destructive)' }}
                          data-testid={`cashflow-dest-remove-${source.id}-${idx}`}
                        />
                      </div>
                    ))
                  )}

                  <div>
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => addDestination(source.id)}
                      data-testid={`cashflow-dest-add-${source.id}`}
                    >
                      Ajouter un compte
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex items-end justify-end">
            <Button size="sm" iconName="Save" iconPosition="left" onClick={handleSubmit} data-testid="cashflow-initial-save">
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-3">
        Les pourcentages sont normalisés automatiquement. Si vous sélectionnez un compte “Assurance vie”, la portion assurance est plafonnée par la prime annuelle courante (et consomme du cash).
      </p>
    </div>
  );
};

export default CashflowDirectionInitial;

